/**
 * Settings → Navigation — switch sidebar pages on/off and drag them into
 * the order you want.
 *
 * The list is the rail's OWN gated tree (`getGatedNavTree`), not a second
 * hand-maintained copy: a page you can't route to is never offered here,
 * and a page added to the rail shows up in this panel the same day.
 *
 * The panel chrome and the drag behaviour are the shared @techstack/ui
 * `NavCustomizer` — the GAS dashboard renders the same component against
 * the same `NavPrefs` blob, so "hidden" means one thing in both shells.
 */

import React from 'react';
import { NavCustomizer } from '@techstack/ui';
import { navChildKey } from '@techstack/core';
import '@techstack/ui/dist/styles/nav-customizer.css';
import { useRole } from '../../hooks/useRole';
import { getGatedNavTree } from '../NavSidebar/navItems';
import { useNavPrefs, hasNavPrefsAccount } from '../../hooks/useNavPrefs';

function toCustomizerItem(item, parentKey) {
  const Icon = item.icon;
  return {
    key: parentKey ? navChildKey(parentKey, item.id) : item.id,
    label: item.label,
    icon: Icon ? <Icon size={16} /> : null,
    children: item.children
      ? item.children.map((child) => toCustomizerItem(child, item.id))
      : undefined,
  };
}

function Navigation() {
  // Same source the rail itself reads — Settings must not gate the list
  // any differently than NavSidebar does, or the panel offers pages the
  // sidebar will never render.
  const { role, isCustomer, isSuperAdmin, isFeatureEnabled } = useRole();
  const { prefs, setPrefs, saving, error } = useNavPrefs();

  const ctx = {
    role,
    isSuperAdmin: !!isSuperAdmin,
    isCustomer: !!isCustomer,
    isFeatureEnabled: isFeatureEnabled || (() => true),
  };

  const items = getGatedNavTree(ctx).map((item) => toCustomizerItem(item, null));

  const status = error
    ? `Not saved to your account: ${error}`
    : saving
      ? 'Saving…'
      : hasNavPrefsAccount()
        ? 'Saved automatically'
        : 'Saved on this device only';

  return (
    <div className="settings-section">
      <h2>Navigation</h2>
      <p className="settings-desc">
        Choose which pages appear in your sidebar and what order they sit in.
        This is yours alone — it changes nothing for anyone else on the team.
      </p>

      <NavCustomizer
        items={items}
        prefs={prefs}
        onChange={setPrefs}
        busy={saving}
        status={status}
        title="Sidebar pages"
        subtitle={
          <>
            Drag a row (or use the arrows) to reorder. Switch a page off to
            hide it from the rail — it stays listed here, and its URL keeps
            working if you have it bookmarked.
          </>
        }
      />
    </div>
  );
}

export default Navigation;
